import ReactDOM from "react-dom";
import WRAP from "WRAP-JS";
import axios from "axios";
import { getCustomerSettings } from "../../../CustomerSettings";
import * as FPEUtils from "../../../utils/FPEUtils";
import imgPlaneWhite from "../../../images/airplane_white.png";
import imgPlaneBlack from "../../../images/airplane_black.png";
import imgPlaneShadow from "../../../images/airplane_shadow.png";
import { MAP_ID_FLIGHTPLAN } from "../../../store/MapContentsState";
import { getCycleDate } from "../../../Authorization";

class FlightPlan {
	static NAME = MAP_ID_FLIGHTPLAN;

	mapDiv;
	routeLayer;
	routePointLayer;
	routeDistanceLayer;
	routePlaneLayer;
	routeHilightLayer;
	routeList;
	routeFixList;
	labelList;
	editing = false;
	lockEvent = false;
	originalPoint = null;
	currentPopup = null;
	originalPlan = null;
	overlayListenerFunc = null;
	planUpdateCallbackFunc = null;
	hilightPointsCallbackFunc = null;
	elapsedTime = 0;
	cycleDate = getCycleDate();

	constructor(mapColor) {
		this.mapColor = mapColor;
		this.initLayer();
	}

	initLayer() {
		this.routeLayer = new WRAP.Geo.Layer("Route");
		this.routePointLayer = new WRAP.Geo.Layer("Route_Point");
		this.routeDistanceLayer = new WRAP.Geo.Layer("Route_Distance");
		this.routePlaneLayer = new WRAP.Geo.Layer("Route_Plane");
		this.routeHilightLayer = new WRAP.Geo.Layer("Route_Highlight");
		this.routeDistanceLayer.disableInteraction(true);
		// this.routePlaneLayer.disableInteraction(true);
		this.routeHilightLayer.disableInteraction(true);
	}

	initInteraction() {
		this.mapDiv = WRAP.Geo.currentView().map.div;

		let dragFlg;
		this.mapDiv.addEventListener('mousedown', event => {
			if(this.lockEvent) {
				return;
			}
			if(this.mouseover && (this.mouseover.editable || this.mouseover.permeate)) {
				const set = (event, point) => {
					const rect = this.mapDiv.getBoundingClientRect();
					let x = event.clientX - rect.left;
					let y = event.clientY - rect.top;
					if(x < 0) {
						x = 0;
					} else if(x > rect.width) {
						x = rect.width;
					}
					if(y < 0) {
						y = 0;
					} else if(y > rect.height) {
						y = rect.height;
					}
					const sp = {x, y};
					const p = WRAP.Geo.getPoint(sp);
					point[0] = p.lonDegree();
					point[1] = p.latDegree();
					WRAP.Geo.invalidate();
				}
				const mousemove = event => {
					if(this.lockEvent) {
						return;
					}
					const x = event.clientX;
					const y = event.clientY;
					this.mousePosition = {x, y};
					set(event, point);
				}
				this.mapDiv.classList.add('drag');

				let feature = this.mouseover;
				const view = WRAP.Geo.currentView();
				view.enableScroll(false);
				const style = feature.style();

				let point = undefined;
				if(feature instanceof WRAP.Geo.Feature.GeoLine) {
					point = [];
					style.path.splice(1, style.path.length - 2, point);
				} else if(feature instanceof WRAP.Geo.Feature.Point) {
					if(feature.editable) {
						point = style.point;
					} else if(feature.permeate) {
						const index = feature.index;
						const features = this.routeLayer.getDisplayList();
						for(const f of features) {
							if(f instanceof WRAP.Geo.Feature.GeoLine) {
								if(f.index === index) {
									feature = f;
									point = [];
									const style = f.style();
									style.path.splice(1, style.path.length - 2, point);
									break;
								}
							}
						}
					}
				} else {
					return;
				}

				this.routeLayer.disableInteraction(true);
				this.routePointLayer.disableInteraction(true);
				this._onDragStart(feature);
				this._switchAutoMapScroll(true);
				set(event, point);
				dragFlg = true;
				event.stopPropagation();

				let mouseupHandle;
				window.addEventListener('mousemove', mousemove, false);

				window.addEventListener('mouseup', mouseupHandle = event => {
					if(this.lockEvent) {
						return;
					}
					window.removeEventListener('mousemove', mousemove);
					window.removeEventListener('mouseup', mouseupHandle);
					this.mapDiv.classList.remove('drag');
					if(dragFlg) {
						dragFlg = false;
						this.lockEvent = true;
						this._onDragEnd(feature, point);
						this._switchAutoMapScroll(false);
						this.routeLayer.disableInteraction(false);
						this.routePointLayer.disableInteraction(false);
					}
				}, false);
			}
		}, true);

		WRAP.Geo.addEventHandler('mouseover', (layer, feature, sp) => {
			if(layer === this.routeLayer) {
				if(feature instanceof WRAP.Geo.Feature.GeoLine) {
					this.mouseover = feature;
					this.mapDiv.style.cursor = "pointer";
					const currentRoute = this.routeList[feature.index];
					const beforeRoute = this.routeList[feature.index - 1];
					if(['fix', 'arr'].includes(currentRoute.type)) {
						if(typeof this.hilightPointsCallbackFunc === "function") {
							this.hilightPointsCallbackFunc([{
								source: 'horizontal',
								type: 'route',
								name: beforeRoute.name,
								lon: beforeRoute.coordinate[0],
								lat: beforeRoute.coordinate[1],
								index: feature.index - 1
							}, {
								source: 'horizontal',
								type: 'route',
								name: currentRoute.name,
								lon: currentRoute.coordinate[0],
								lat: currentRoute.coordinate[1],
								index: feature.index
							}]);
						}
					} else if(currentRoute.type === 'airway') {
						if(typeof this.hilightPointsCallbackFunc === "function") {
							this.hilightPointsCallbackFunc(
								currentRoute.fixes.map(fix => {
									return {
										source: 'horizontal',
										type: 'airway',
										name: fix.name,
										lon: fix.coordinate[0],
										lat: fix.coordinate[1],
										index: feature.index
									};
								})
							);
						}
					}
				}
			} else if(layer === this.routePointLayer) {
				if(feature instanceof WRAP.Geo.Feature.Point) {
					this.mouseover = feature;
					if(feature.editable) {
						this.mapDiv.style.cursor = "move";
					} else if(feature.permeate) {
						this.mapDiv.style.cursor = "pointer";
					}
					if(feature.data) {
						const data = feature.data;
						if(typeof this.hilightPointsCallbackFunc === "function") {
							this.hilightPointsCallbackFunc([{
								source: 'horizontal',
								type: 'fix',
								name: data.name,
								lon: data.coordinate[0],
								lat: data.coordinate[1],
								index: feature.index
							}]);
						}
					}
				}
			} else if(layer === this.routePlaneLayer) {
				if(feature.data && feature.data.type === 'plane') {
					if(typeof this.hilightPointsCallbackFunc === "function") {
						this.hilightPointsCallbackFunc([{
							source: 'horizontal',
							type: 'plane',
							name: 'AirPlane',
							lon: null,
							lat: null,
							index: -1
						}]);
					}
				}
			}
		});
	
		WRAP.Geo.addEventHandler('mouseout', (layer, feature, sp) => {
			if(layer === this.routeLayer) {
				this.mouseover = null;
				if(feature instanceof WRAP.Geo.Feature.GeoLine) {
					this.mapDiv.style.cursor = "auto";
					if(typeof this.hilightPointsCallbackFunc === "function") {
						this.hilightPointsCallbackFunc([]);
					}
				}
			} else if(layer === this.routePointLayer) {
				this.mouseover = null;
				if(feature instanceof WRAP.Geo.Feature.Point) {
					this.mapDiv.style.cursor = "auto";
					if(typeof this.hilightPointsCallbackFunc === "function") {
						this.hilightPointsCallbackFunc([]);
					}
				}
			} else if(layer === this.routePlaneLayer) {
				if(typeof this.hilightPointsCallbackFunc === "function") {
					this.hilightPointsCallbackFunc([]);
				}
			}
		});

		WRAP.Geo.addEventHandler('boundsChange', () => {
			this._updateBounds();
		});
	}

	getLayer() {
		return [this.routeLayer, this.routeDistanceLayer, this.routePointLayer, this.routePlaneLayer, this.routeHilightLayer];
	}

	async drawLayer(visible) {
		this.setVisible(visible);
	}

	setVisible(visible) {
		if (visible) {
			this.routeLayer.show();
			this.routePointLayer.show();
			this.routeDistanceLayer.show();
			this.routePlaneLayer.show();
			this.routeHilightLayer.show();
		} else {
			this.routeLayer.hide();
			this.routePointLayer.hide();
			this.routeDistanceLayer.hide();
			this.routePlaneLayer.hide();
			this.routeHilightLayer.hide();
		}
	}

	onPlanUpdate(func) {
		this.planUpdateCallbackFunc = func;
	}

	onHilightPoints(func) {
		this.hilightPointsCallbackFunc = func;
	}

	onError(func) {
		this.errorFunc = func;
	}

	hilightPoints(points) {
		if(!points || points.length === 0) {
			this.routeHilightLayer.clear();
			if(this.planeOverlay) {
				this.planeOverlay.classList.remove('highlight');
			}
		} else if(!this.editing) {
			this.routeHilightLayer.clear();
			if(points.length === 1) {
				const point = points[0];
				if(point.source !== 'horizontal') {
					switch(point.type) {
						case 'fix':
							this.routeHilightLayer.addFeature(new WRAP.Geo.Feature.Point({
								point: [point.lon, point.lat],
								strokeStyle:'#880',
								lineWidth: 2,
								fillStyle: '#ff7b00',
								pointSize: 14
							}));
							break;
						case 'plane':
							if(this.planeOverlay) {
								this.planeOverlay.classList.add('highlight');
							}
							break;
						default:
							break;
					}
				}
			} else {
				if(points[0].source !== 'horizontal') {
					this.routeHilightLayer.addFeature(new WRAP.Geo.Feature.GeoLine({
						path: points.map(p => [p.lon, p.lat]),
						strokeStyle: "rgba(255, 123, 0, 0.5)",
						lineWidth: 6,
						method: "GC"
					}));
				}
			}
		}
		WRAP.Geo.invalidate();
	}

	setTime(elapsedTime) {
		this.elapsedTime = elapsedTime;
		this._drawPlane();
	}

	parsePlan(data) {
		if(!data || !data.type || data.type !== "FPL") {
			this._clearRoute();
			return;
		}

		const routeList = [];
		routeList.push({
			"coordinate": [data.dep.lon-0, data.dep.lat-0],
			"name": data.dep.icao,
			"type": "dep",
			"alt": 0,
			"speed": data.dep.speed,
			"refs": FPEUtils.deepCopy(data.dep)
		});
		data.route.forEach(d => {
			let fix;
			if(!d.airway) {
				fix = {
					"coordinate": [d.lon-0, d.lat-0],
					"name": d.name,
					"type": "fix",
					"dist": d.dist,
					"time": d.time,
					"speed": d.speed,
					"alt": d.level
				};
			} else {
				const fixes = [];
				let dist = 0;
				let time = 0;
				d.airway.forEach(a => {
					fixes.push({
						"coordinate": [a.lon-0, a.lat-0],
						"name": a.name,
						"dist": a.dist,
						"time": a.time,
						"speed": a.speed,
						"alt": a.level,
						"minAlt": a.minAlt,
						"maxAlt": a.maxAlt
					});
					dist += a.dist;
					time += a.time;
				});
				fix = {
					"fixes": fixes,
					"name": d.airwayName,
					"type": "airway",
					"dist": dist,
					"time": time
				};
			}
			fix.refs = FPEUtils.deepCopy(d);
			routeList.push(fix);
		});
		routeList.push({
			"coordinate": [data.arr.lon-0, data.arr.lat-0],
			"name": data.arr.icao,
			"type": "arr",
			"dist": data.arr.dist,
			"time": data.arr.time,
			"alt": 0,
			"refs": FPEUtils.deepCopy(data.arr)
		});
		this.routeList = routeList;
		this.originalPlan = data;
		this._makeFiexes();
		this._redraw();
		this._drawPlane();
	}

	changeProjection() {
		this._redraw();
		this._drawPlane();
	}

	applyMapColor(mapColor) {
		this.mapColor = mapColor;
		this._redraw();
		this._drawPlane();
	}

	_updateBounds() {
		this._redrawLabel();
	}

	_makeFiexes() {
		const routeFixList = [];
		let count = 0;
		this.routeList.forEach(route => {
			if(route.type === 'airway') {
				for(let i = 1; i < route.fixes.length - 1; i++) {
					const fix = route.fixes[i];
					fix.fixindex = count++;
					fix.type = 'airway-fix';
					routeFixList.push(fix);
				}
			} else {
				route.fixindex = count++;
				routeFixList.push(route);
			}
		});
		this.routeFixList = routeFixList;
	}

	_updatePlan() {
		if(!this.planUpdateCallbackFunc) {
			return;
		}

		const plan = FPEUtils.deepCopy(this.originalPlan);
		const routes = [];
		plan.route = routes;
		this.routeList.forEach(route => {
			const o = route.refs ? FPEUtils.deepCopy(route.refs) : {};
			if(["fix", "gps"].includes(route.type)) {
				o.lon = route.coordinate[0];
				o.lat = route.coordinate[1];
				o.name = route.name ? route.name : "";
				o.dist = route.dist;
				o.time = route.time;
				o.speed = route.speed;
				o.level = route.alt;
				routes.push(o);
			} else if(route.type === "dep") {
				o.lon = route.coordinate[0];
				o.lat = route.coordinate[1];
				o.icao = route.name;
				o.speed = route.speed;
				plan.dep = o;
			} else if(route.type === "arr") {
				o.lon = route.coordinate[0];
				o.lat = route.coordinate[1];
				o.icao = route.name;
				o.dist = route.dist;
				o.time = route.time;
				plan.arr = o;
			} else if(route.type === "airway") {
				route.fixes.forEach((f, i) => {
					o.airway[i].lon = f.coordinate[0];
					o.airway[i].lat = f.coordinate[1];
					o.airway[i].name = f.name;
					o.airway[i].dist = f.dist;
					o.airway[i].time = f.time;
					o.airway[i].speed = f.speed;
					o.airway[i].level = f.alt;
					o.airway[i].minAlt = f.minAlt;
					o.airway[i].maxAlt = f.maxAlt;
				});
				routes.push(o);
			} else {
				routes.push(o);
			}
		});
		// console.log(JSON.stringify(plan, "", "\t"));
		this.planUpdateCallbackFunc(plan);
	}

	_nextFix(index, direction) {
		let i = index;
		let delta;
		if(direction > 0 || direction === true) {
			delta = 1;
		} else {
			delta = -1;
		}

		while(true) {
			i += delta;
			if(delta === 1 && i >= this.routeList.length) {
				return null;
			}
			if(delta === -1 && i < 0) {
				return null;
			}

			if(['dep', 'arr', 'fix'].includes(this.routeList[i].type)) {
				return this.routeList[i];
			}
		}

	}

	_switchAutoMapScroll(flg) {
		if(flg) {
			this.mousePosition = {};
			if(!(this.scrollTimer > 0)) {
				this.scrollTimer = setInterval(() => {
					if(this.mousePosition.x === undefined || this.mousePosition.y === undefined) {
						return;
					}
					const rect = this.mapDiv.getBoundingClientRect();
					const x = this.mousePosition.x - rect.left;
					const y = this.mousePosition.y - rect.top;
					let dx = 0;
					let dy = 0;
					const SCROLL_HIT_SIZE = 25;
					const SCROLL_SIZE = 4;
					if(x <= SCROLL_HIT_SIZE) {
						dx = -SCROLL_SIZE;
					} else if(rect.width - x <= SCROLL_HIT_SIZE) {
						dx = SCROLL_SIZE;
					}
					if(y <= SCROLL_HIT_SIZE) {
						dy = -SCROLL_SIZE;
					} else if(rect.height - y <= SCROLL_HIT_SIZE) {
						dy = SCROLL_SIZE;
					}
					if(dx !==0 || dy !==0) {
						const centerPoint = WRAP.Geo.getCenterPoint();
						const screenCenterPoint = WRAP.Geo.getScreenPoint(centerPoint);
						const movedCenterPoint = WRAP.Geo.getPoint(new WRAP.Geo.ScreenPoint(screenCenterPoint.x + dx, screenCenterPoint.y + dy));
						WRAP.Geo.setCenterPoint(movedCenterPoint);
						const mouseEvent = new MouseEvent("mousemove", {
							clientX: this.mousePosition.x,
							clientY: this.mousePosition.y
						});
						window.dispatchEvent(mouseEvent);
					}
				}, 17);
			}
		} else {
			if(this.scrollTimer > 0) {
				clearInterval(this.scrollTimer);
				this.scrollTimer = 0;
			}
		}

	}

	_onDragStart(feature) {
		this.editing = true;
		const index = feature.index;
		if(feature instanceof WRAP.Geo.Feature.Point && feature.editable) {
			this.originalPoint = Object.assign({}, this.routeList[index]);
			this.originalPoint.coordinate = this.originalPoint.coordinate.slice();
			const features = this.routeLayer.getDisplayList();
			let a = [], b = [];
			for(const feature of features) {
				if(feature instanceof WRAP.Geo.Feature.GeoLine) {
					if(feature.index === index - 1) {
						a.push(feature);
					} else if(feature.index === index + 1) {
						b.push(feature);
					}
				}
				if(a.length === 2 && b.length === 2) break;
			}
			a.forEach(_a => {
				if (_a.type === "airway") {
					const as = _a.style();
					as.path = [this.routeList[index - 2].coordinate, this.routeList[index].coordinate];
				}
			});
			b.forEach(_b => {
				if(_b.type === "airway") {
					const bs = _b.style();
					bs.path = [this.routeList[index].coordinate, this.routeList[index + 2].coordinate];
				}
			});
			let style = feature.style();
			style.fillStyle = '#880';
		}
		this.routeDistanceLayer.setVisible(false);
	}

	_onDragEnd(feature, coordinate) {
		const index = feature.index;
		let mode;
		if(feature instanceof WRAP.Geo.Feature.GeoLine) {
			mode = 'line';
		} else if(feature instanceof WRAP.Geo.Feature.Point) {
			mode = 'point';
		}
		const params = {
			lon: coordinate[0],
			lat: coordinate[1],
			cycleDate: this.cycleDate
		};
		axios.get("cgi/near_fix.cgi",
			{
				params : params
			})
			.then(response => response.data)
			.then(data => {
				const OFFSET_X = 50;
				const OFFSET_Y = 6;
				this.currentPopup = this._createPopup(data, index, coordinate, mode);
				const pw = this.mapDiv.clientWidth;
				const ph = this.mapDiv.clientHeight;
				const sp = WRAP.Geo.getScreenPoint(FPEUtils.convertWRAPPoint(coordinate[1], coordinate[0]));
				let left, right, top, bottom;
				if(pw / 2 > sp.x) {
					left = (sp.x + OFFSET_X) + 'px';
					right = 'auto';
				} else {
					left = 'auto';
					right = (pw - sp.x + OFFSET_X) + 'px';
				}
				if(ph / 2 > sp.y) {
					top = (sp.y + OFFSET_Y) + 'px';
					bottom = 'auto';
				} else {
					top = 'auto';
					bottom = (ph - sp.y + OFFSET_Y) + 'px';
				}
				Object.assign(this.currentPopup.style, {
					left,
					right,
					top,
					bottom
				});
				this.mapDiv.appendChild(this.currentPopup);
				overlay.addEventListener('click', this.overlayListenerFunc);
			})
			.catch(e => {
				this.errorFunc('Failed to retrieve FIX', e, params);
				if (overlay) {
					this.overlayListenerFunc();
				}
			});
		const overlay = document.createElement('div');
		overlay.id = 'overlay';
		overlay.style.position = 'absolute';
		overlay.style.left = 0;
		overlay.style.top = 0;
		overlay.style.width = '100%';
		overlay.style.height = '100%';
		overlay.style.backgroundColor = 'rgba(128, 128, 128, 0.5)';
		overlay.style.zIndex = 1000;
		this.overlayListenerFunc = event => {
			overlay.remove();
			if(this.currentPopup !== null) {
				this.mapDiv.removeChild(this.currentPopup);
				this.currentPopup = null;
			}
			if(this.editing) {
				if(this.originalPoint) {
					this.routeList.splice(index, 1, this.originalPoint);
				}
				this.editing = false;
				this.originalPoint = null;
				this.routeDistanceLayer.setVisible(true);
			}
			this._makeFiexes();
			this._redraw();
			this.lockEvent = false;
		}
		document.getElementById('map').appendChild(overlay);
	}

	_createPopup(data, index, coordinate, mode) {
		const previewPoint = new WRAP.Geo.Feature.Point({
			point: [0,0],
			strokeStyle:'#000',
			lineWidth: 1,
			fillStyle: 'orange',
			pointSize: 10
		});
		const onMouseEnter = event => {
			const target = event.currentTarget;
			previewPoint.style().point = [target.dataset.lon-0, target.dataset.lat-0];
			this.routePointLayer.addFeature(previewPoint);
			WRAP.Geo.invalidate();
		};
		const onMouseLeave = event => {
			this.routePointLayer.removeFeature(previewPoint);
			WRAP.Geo.invalidate();
		};
		const onClick = event => {
			const overlay = document.getElementById('overlay');
			overlay.removeEventListener('click', this.overlayListenerFunc);
			const target = event.currentTarget;
			const trimAirway = index => {
				if(index < 0 || index >= this.routeList.length) {
					return false;
				}
				if(this.routeList[index].type === "airway") {
					this.routeList.splice(index, 1);
					return true;
				}
				return false;
			}
			const copyProperties = (copy, from) => {
				["level", "speed", "levelOpt"].forEach(key => {
					if(key in from) {
						copy[key] = from[key];
					}
				});
			}
			let currentIndex = index;
			const command = target.getAttribute("command");
			let currentPoint;
			let fix1 = null;
			let fix2 = null;
			let type = null;
			if(command === 'del') {
				fix1 = this._nextFix(currentIndex, -1);
				this.routeList.splice(currentIndex, 1);
				currentPoint = this._nextFix(currentIndex - 1, 1);
			} else if(['fix', 'gps'].includes(command)) {
				currentPoint = {};
				currentPoint.coordinate = [target.dataset.lon-0, target.dataset.lat-0];
				if(command === 'fix') {
					currentPoint.name = target.dataset.ident;
				} else if(command === 'gps') {
					if(!currentPoint.coordinate.every(Number.isInteger)) {
						currentPoint.name = FPEUtils.formatLonLatATC1.apply(null, currentPoint.coordinate);
					} else {
						currentPoint.name = FPEUtils.formatLonLatATC2.apply(null, currentPoint.coordinate);
					}
				}
				fix1 = this._nextFix(currentIndex, -1);
				fix2 = this._nextFix(currentIndex + (mode === 'line' ? -1 : 0), 1);
				type = command;
			} else {
				// cancel
				if(currentIndex !== -1) {
					if(this.originalPoint) {
						this.routeList.splice(currentIndex, 1, this.originalPoint);
					}
				}
				this.editing = false;
				this.originalPoint = null;
				this.routeDistanceLayer.setVisible(true);
				document.getElementById('overlay').remove();
				this.mapDiv.removeChild(this.currentPopup);
				this.currentPopup = null;
				this._makeFiexes();
				this._redraw();
				this._updatePlan();
				this.lockEvent = false;
				return;
			}
			const params = {
				fix: currentPoint.name,
				lon: currentPoint.coordinate[0],
				lat: currentPoint.coordinate[1],
				alt: this.routeList[currentIndex].alt ? this.routeList[currentIndex].alt : fix1.alt
			};
			if(fix1) {
				Object.assign(params, {
					fix1: fix1.name,
					lon1: fix1.coordinate[0],
					lat1: fix1.coordinate[1],
					alt1: fix1.alt,
				});
			}
			if(fix2) {
				Object.assign(params, {
					fix2: fix2.name,
					lon2: fix2.coordinate[0],
					lat2: fix2.coordinate[1],
					alt2: fix2.alt,
				});
			}
			Object.assign(params, { cycleDate: this.cycleDate });
			axios.get("cgi/plan_route.cgi",
			{
				params: params
			})
			.then(response => response.data)
			.then(data => {
				if(mode === 'line') {
					if(this.routeList[currentIndex].type === 'airway') {
						this.routeList[currentIndex] = {};
					} else {
						this.routeList.splice(currentIndex, 0, {});
					}
				} else {
					let offset = 1;
					if(command === 'del') {
						offset = 0;
					}
					trimAirway(currentIndex - 1) && currentIndex--;
					trimAirway(currentIndex + offset);
				}

				if(command !== 'del') {
					Object.assign(this.routeList[currentIndex], {
						"coordinate": currentPoint.coordinate,
						"name": currentPoint.name,
						"type": type,
					});
				}

				data.forEach((v, i) => {
					let index = currentIndex + i;
					const currentRoute = this.routeList[index];
					const beforeRoute = this.routeList[index - 1];
					let dist, time;
					if(v.airway) {
						this.routeList.splice(index, 0, {
							name: v.airwayName,
							refs: {
								airwayName: v.airwayName,
								airway: v.airway.map(a => {
									let o = Object.assign(a);
									copyProperties(o, beforeRoute.refs);
									return o;
								})
							},
							type: "airway",
							fixes: v.airway.map((v, i, a) => {
								const ret = {
									"coordinate": [v.lon-0, v.lat-0],
									"name": v.name,
									"dist": v.dist,
									"time": v.dist / beforeRoute.speed,
									"speed": beforeRoute.speed,
									"alt": beforeRoute.alt,
									"minAlt": v.minAlt,
									"maxAlt": v.maxAlt,
								};
								if(i === a.length - 1) {
									if(currentRoute.speed !== undefined) {
										ret.speed = currentRoute.speed;
									}
									if(currentRoute.alt !== undefined) {
										ret.alt = currentRoute.alt;
									}
								}
								return ret;
							})
						});
						currentIndex++;
						index++;
						dist = 0;
						time = 0;
					} else {
						dist = v.dist;
						time = v.dist / beforeRoute.speed;
					}
					currentRoute.dist = dist;
					currentRoute.time = time;
					if(i === 0) {
						if(currentRoute.speed === undefined) {
							currentRoute.speed = beforeRoute.speed;
						}
						if(currentRoute.alt === undefined) {
							currentRoute.alt = beforeRoute.alt;
						}
						if(!currentRoute.refs) {
							currentRoute.refs = {};
						}
						copyProperties(currentRoute.refs, beforeRoute.refs);
					}
				});

				this.editing = false;
				this.originalPoint = null;
				this.routeDistanceLayer.setVisible(true);
				document.getElementById('overlay').remove();
				this._makeFiexes();
				this._redraw();
				this._updatePlan();
				this.lockEvent = false;
			})
			.catch(e => {
				this.errorFunc('Failed to apply the route', e, params);
				this.overlayListenerFunc();
			});
			this.mapDiv.removeChild(this.currentPopup);
			this.currentPopup = null;
		};
		const list = data.map(d => {
			return (
				<li
					key={d['ident']}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
					onClick={onClick}
					command="fix"
					data-ident={d['ident']}
					data-lon={d['lon']}
					data-lat={d['lat']}
				>
					<ul className="options"><li className="command">FIX</li><li>{d['ident']}
						{d['airway'] && <span> ({d['airway']})</span>}
					</li></ul>
				</li>
			);
		});
		const SNAP_GRID_DIST = 10;
		const gridLon = Math.round(coordinate[0]);
		const gridLat = Math.round(coordinate[1]);
		const spOrg = WRAP.Geo.getScreenPoint(FPEUtils.convertWRAPPoint(coordinate[1], coordinate[0]));
		const spGrid = WRAP.Geo.getScreenPoint(FPEUtils.convertWRAPPoint(gridLat, gridLon));
		if(Math.pow(spOrg.x - spGrid.x, 2) + Math.pow(spOrg.y - spGrid.y, 2) <= Math.pow(SNAP_GRID_DIST, 2)) {
			const lonlatStr = FPEUtils.formatLonLatDM(gridLon, gridLat);
			list.push(
				<li
					key="grid"
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
					onClick={onClick}
					command="gps"
					data-lon={gridLon}
					data-lat={gridLat}
				>
					<ul className="options"><li className="command">GPS</li><li>{lonlatStr}</li></ul>
				</li>
			);
		}
		const lonlatStr = FPEUtils.formatLonLatDM(coordinate[0], coordinate[1]);
		const popup = document.createElement('div');
		popup.id = 'popup';
		ReactDOM.render(
			<>
				<div className="title">Location Information</div>
				<ul className="options_root">
					{list}
					<li
						onMouseEnter={onMouseEnter}
						onMouseLeave={onMouseLeave}
						onClick={onClick}
						command="gps"
						data-lon={coordinate[0]}
						data-lat={coordinate[1]}
					>
						<ul className="options"><li className="command">GPS</li><li>{lonlatStr}</li></ul>
					</li>
					<li
						onClick={onClick}
						command={mode === 'line' ? 'cancel' : 'del'}
					>
						<ul className="options"><li className="command">DEL</li><li>Delete Waypoint</li></ul>
					</li>
				</ul>
			</>
		, popup);
		return popup;
	}

	_redraw() {
		if(!this.routeList) {
			return;
		}

		this.routeLayer.clear();
		this.routePointLayer.clear();
		this.routeDistanceLayer.clear();
		this.labelList = {fix:[], dist:[]};

		const lineFeatures = [];
		const outerLineFeatures = [];
		const pointFeatures = [];
		const airwayPointFeatures = [];

		for(let i = 0; i < this.routeList.length; i++) {
			const currentRoute = this.routeList[i];
			if(i > 0) {
				const beforeRoute = this.routeList[i - 1];

				// Route Line
				let coordinate = null;
				if(['dep', 'fix', 'gps'].includes(beforeRoute.type) && ['arr', 'fix', 'gps'].includes(currentRoute.type)) {
					coordinate = [ beforeRoute.coordinate, currentRoute.coordinate ];
				} else if(currentRoute.type === 'airway') {
					coordinate = currentRoute.fixes.map(f => f.coordinate);
				}
				if(coordinate !== null) {
					const outerLineFeture = new WRAP.Geo.Feature.GeoLine({
						path: coordinate,
						// strokeStyle: "rgba(255, 255, 255, 0.5)",
						strokeStyle: this.mapColor === "white" ? "rgba(128, 128, 128, 1)" : "rgba(240, 240, 240, 1)",
						lineWidth: 9,
						method: "GC"
					});
					outerLineFeture.draggable = true;
					outerLineFeture.index = i;
					outerLineFeture.type = currentRoute.type;
					outerLineFeture.editable = true;
					outerLineFeatures.push(outerLineFeture);

					const lineFeture = new WRAP.Geo.Feature.GeoLine({
						path: coordinate,
						// strokeStyle: "rgba(224, 64, 208, 0.5)",
						strokeStyle: "rgba(239, 159, 231, 1)",
						lineWidth: 6,
						method: "GC"
					});
					lineFeture.draggable = true;
					lineFeture.index = i;
					lineFeture.type = currentRoute.type;
					lineFeture.editable = true;
					lineFeatures.push(lineFeture);
				}

				// Distance Label
				let distance;
				coordinate = null;
				if(['dep', 'fix', 'gps'].includes(beforeRoute.type) && ['arr', 'fix', 'gps'].includes(currentRoute.type)) {
					distance = currentRoute.dist;
					coordinate = currentRoute.coordinate;
				} else if(currentRoute.type === 'airway') {
					distance = currentRoute.dist;
					coordinate = currentRoute.fixes[currentRoute.fixes.length - 1].coordinate;
				}
				if(getCustomerSettings("PlanRoute.Distance") && distance !== undefined) {
					this.labelList.dist.push({type: "distance", route: currentRoute, text: this._toDistString(distance), point: coordinate});
				}
			}

			// Route Point
			if(['dep', 'arr', 'fix', 'gps'].includes(currentRoute.type)) {
				let style;
				if(['dep', 'arr'].includes(currentRoute.type)) {
					style = {
						strokeStyle: false,
						pointSize: 8,
						fillStyle: "#888"
					}
				} else {
					style = {
						strokeStyle:'#008',
						lineWidth: 2,
						fillStyle: '#FFF',
						pointSize: 12
					}
				}
				const pointFeture = new WRAP.Geo.Feature.Point(
					Object.assign({
						point: currentRoute.coordinate,
					}, style)
				);
				pointFeture.index = i;
				pointFeture.data = currentRoute;
				pointFeatures.push(pointFeture);
				if(!['dep', 'arr'].includes(currentRoute.type)) {
					pointFeture.editable = true;
				}
			} else if(currentRoute.type === 'airway') {
				for(let j = 1; j < currentRoute.fixes.length - 1; j++) {
					const pointFeture = new WRAP.Geo.Feature.Point({
						point: currentRoute.fixes[j].coordinate,
						pointSize: 8,
						strokeStyle: false,
						fillStyle: "#888"
					});
					pointFeture.index = i;
					pointFeture.data = currentRoute.fixes[j];
					pointFeture.permeate = true;
					airwayPointFeatures.push(pointFeture);
				}
			}
		}

		// Fix Label
		this.routeFixList.forEach((fix, idx, org) => {
			const p1 = idx !== 0 ? org[idx - 1] : org[0];
			const p2 = idx !== org.length - 1 ? org[idx + 1] : org[org.length - 1];
			const angle = FPEUtils.calcMiddleAngle(fix.coordinate, p1.coordinate, p2.coordinate);
			this.labelList.fix.push({type: fix.type, route: fix, text: fix.name, point: fix.coordinate, angle: angle});
		});

		outerLineFeatures.forEach(f => this.routeLayer.addFeature(f));
		lineFeatures.forEach(f => this.routeLayer.addFeature(f));
		airwayPointFeatures.reverse().forEach(f => this.routePointLayer.addFeature(f));
		pointFeatures.reverse().forEach(f => this.routePointLayer.addFeature(f));

		this._redrawLabel();

		WRAP.Geo.invalidate();
	}

	_redrawLabel() {
		if(!this.labelList) {
			return;
		}
		const fontSize = 12;
		this.routeDistanceLayer.clear();
		// const mask = WRAP.Geo.getMask();
		const mask = new WRAP.Geo.Mask(WRAP.Geo.currentView());
		mask.clear();
		const features = [];
		const make = (l, type, deparr) => {
			if(!l || !l.text) {
				return;
			}
			let flg = false;
			const textSize = WRAP.Geo.getTextMetrics(l.text, fontSize);
			const w = textSize[0];
			const h = textSize[1];
			const candidates = (() => {
				let angle = 90;
				if(l.angle !== undefined) {
					angle = FPEUtils.normalizeDegree(l.angle);
					if(deparr) {
						if(angle > 180) {
							angle = 90;
						} else if(angle <= 90) {
							angle = 180;
						} else {
							angle = 0;
						}
					} else {
						while(angle > 180) {
							angle -= 180;
						}
					}
				}
				if(type === 'fix') {
					let candidates;
					if(angle < 90) {
						candidates = [[angle, 10], [angle - 45, 10], [angle + 45, 10]];
					} else {
						candidates = [[angle, 10], [angle + 45, 10], [angle - 45, 10]];
					}
					return candidates.filter(v => v[0] >= 0 && v[0] <= 180);
				} else {
					return [[-90, 10]];
				}
			})();
			let x, y;
			let tx, ty;
			const margin = 2;
			const fixY = -2; //Y軸は少し浮いて見えるため調整
			for(let i = 0; i < candidates.length; i++) {
				const candidate = candidates[i];
				const r = candidate[1];
				const deg = FPEUtils.normalizeDegree(candidate[0]);
				const rad = FPEUtils.deg2rad(deg);
				x = Math.cos(rad) * r;
				y = Math.sin(rad) * r;
				if(deg >= 0 && deg <= 60) {
					tx = x + margin;
					ty = y + h / 2 + fixY;
				} else if(deg > 60 && deg < 120) {
					tx = x - w / 2;
					ty = y + h + margin + fixY;
				} else if(deg >= 120 && deg <= 180) {
					tx = x - w - margin;
					ty = y + h / 2 + fixY;
				} else {
					tx = x - w / 2;
					ty = y + margin + fixY;
				}
				if(deparr || !mask.intersectRect(l.point, tx, ty, w, h)) {
					flg = true;
					mask.addRect(l.point, tx, ty, w, h);
					break;
				}
			}

			if(flg) {
				if(type === 'fix') {
					features.push(new WRAP.Geo.Feature.Line({
						point: l.point,
						line: [[0, 0], [x, y]],
						lineWidth: 1,
						strokeStyle: 'rgba(50,50,50,0.8)'
					}));
				}
				features.push(new WRAP.Geo.Feature.Text({
					point: l.point,
					text: l.text,
					fontSize: fontSize,
					fillStyle: this.mapColor === "white" ? "#000" : "#FFF",
					offsetX: tx,
					offsetY: ty,
					align: "left"
				}));
			}
		}

		// dep/arr は必須
		if(this.labelList.fix.length >= 1) {
			make(this.labelList.fix[0], 'fix', true);
		}
		if(this.labelList.fix.length >= 2) {
			make(this.labelList.fix[this.labelList.fix.length - 1], 'fix', true);
		}
		const lowPriority = [];
		for(let i = 1; i < this.labelList.fix.length - 1; i++) {
			if(this.labelList.fix[i].type === 'airway-fix') {
				lowPriority.push(this.labelList.fix[i]);
			} else {
				make(this.labelList.fix[i], 'fix');
			}
		}
		lowPriority.forEach(label => make(label, 'fix'));

		this.labelList.dist.forEach(label => make(label, 'dist'));

		features.reverse().forEach(f => this.routeDistanceLayer.addFeature(f));
	}

	_drawPlane() {
		if(!this.routeList) {
			return;
		}

		if(!this.planeOverlay) {
			this.planeOverlay = document.createElement('div');
			this.planeOverlay.id = 'plane_overlay';
		}

		let beforePoint, afterPoint;
		let beforeSumTime, afterSumTime = 0;
		const sumTime = array => {
			for(let i = 0; i < array.length; i++) {
				let route = array[i];
				if(route.type === "airway") {
					if(sumTime(route.fixes)) {
						return true;
					}
				} else {
					if(!afterPoint || !FPEUtils.isEqualObject(afterPoint.coordinate, route.coordinate)) {
						beforePoint = afterPoint;
					}
					afterPoint = route;
					beforeSumTime = afterSumTime;
					afterSumTime += route.time ? route.time : 0;
				}
				if(afterSumTime >= this.elapsedTime) {
					return true;
				}
			}
			return false;
		}
		sumTime(this.routeList);
		if(!beforePoint) {
			beforePoint = afterPoint;
			if(this.routeList[1].type === 'airway') {
				afterPoint = this.routeList[1].fixes[1];
			} else {
				afterPoint = this.routeList[1];
			}
			afterSumTime = afterPoint.time;
		}
		const gc = new WRAP.Geo.GreatCircle(beforePoint.coordinate[1], beforePoint.coordinate[0], afterPoint.coordinate[1], afterPoint.coordinate[0]);
		let t = afterSumTime !== beforeSumTime ? (this.elapsedTime - beforeSumTime) / (afterSumTime - beforeSumTime) : 1;
		if(t > 1.0) {
			t = 1.0;
		}
		const pt = gc.getPosition(t);
		const point = [pt.lon, pt.lat];
		const pt0 = gc.getPosition(t - 0.1);
		const pt1 = gc.getPosition(t + 0.1);
		const xy0 = WRAP.Geo.getScreenPoint(FPEUtils.convertWRAPPoint(pt0.lat, pt0.lon));
		const xy1 = WRAP.Geo.getScreenPoint(FPEUtils.convertWRAPPoint(pt1.lat, pt1.lon));
		const xy  = WRAP.Geo.getScreenPoint(FPEUtils.convertWRAPPoint(pt.lat, pt.lon));
		const angle1 = Math.atan2(xy0.y - xy.y, xy0.x - xy.x);
		const angle2 = Math.atan2(xy.y - xy1.y, xy.x - xy1.x);
		const rotation = FPEUtils.rad2deg(Math.atan2(
			Math.sin(angle1) + Math.sin(angle2),
			Math.cos(angle1) + Math.cos(angle2)
		));
		const alt = beforePoint.alt * (1 - t) + afterPoint.alt * t;

		this.routePlaneLayer.clear();
/*
		this.routePlaneLayer.addFeature(new WRAP.Geo.Feature.Point({
			point: beforePoint ? beforePoint : afterPoint,
			strokeStyle:'#880',
			lineWidth: 2,
			fillStyle: '#00F',
			pointSize: 15
		}));
		this.routePlaneLayer.addFeature(new WRAP.Geo.Feature.Point({
			point: afterPoint,
			strokeStyle:'#880',
			lineWidth: 2,
			fillStyle: '#00F',
			pointSize: 15
		}));
		this.routePlaneLayer.addFeature(new WRAP.Geo.Feature.Point({
			point: point,
			strokeStyle:'#880',
			lineWidth: 2,
			fillStyle: '#F00',
			pointSize: 20
		}));
*/
		const shadowCos = Math.cos(FPEUtils.deg2rad(-(rotation - 90)));
		const shadowSin = Math.sin(FPEUtils.deg2rad(-(rotation - 90)));
		this.routePlaneLayer.addFeature(new WRAP.Geo.Feature.Image(
			{
				point: point,
				image: imgPlaneShadow,
				width: 32,
				height: 32,
				offsetX: -16 + (2 + alt / 5000.0) * (shadowCos - shadowSin),
				offsetY: -16 + (2 + alt / 5000.0) * (shadowSin + shadowCos),
				rotation: rotation - 90
			}
		));

		const plane = new WRAP.Geo.Feature.Image(
			{
				point: point,
				image: this.mapColor === "white" ? imgPlaneWhite : imgPlaneBlack,
				width: 32,
				height: 32,
				offsetX: -16,
				offsetY: -16,
				rotation: rotation - 90
			}/*,
			{
				duration: 1,
				action: [
					{
						property: 'width',
						value: [32, 36],
						type: 'value'
					},
					{
						property: 'height',
						value: [32, 36],
						type: 'value'
					},
					{
						property: 'offsetX',
						value: [-16, -18],
						type: 'value'
					},
					{
						property: 'offsetY',
						value: [-16, -18],
						type: 'value'
					},
				]
			}*/
		);
		plane.data = {
			type: 'plane'
		};
		this.routePlaneLayer.addFeature(plane);
		WRAP.Geo.addOverlay(
			this.planeOverlay,
			FPEUtils.convertWRAPPoint(point[1], point[0]),
			0, 0);
		WRAP.Geo.invalidate();
	}

	_toDistString(meter) {
		switch(getCustomerSettings("PlanRoute.DistanceUnit")) {
			case 'km':
			default:
				return (
					Math.round(meter / 1000) + "km"
				);
			case 'nm':
				return (
					Math.round(meter / 1852.0) + "nm"
				);
		}
	}

	_clearRoute() {
		this.routeList = null;
		this.routeFixList = null;
		this.labelList = null;
		this.routeLayer.clear();
		this.routePointLayer.clear();
		this.routeDistanceLayer.clear();
		this.routePlaneLayer.clear();
		if(this.planeOverlay) {
			WRAP.Geo.removeOverlay(this.planeOverlay);
		}
	}
}

export default FlightPlan;
